import Fuse from 'fuse.js';
import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  BillableIcon,
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Currency,
  DisciplineSelect,
  LocationSelect,
  ModalCard,
  PracticeSelect,
  SearchInput,
  Table,
  Tooltip,
} from '~/components';
import FixedDropdown from '~/components/FixedDropdown';
import { Card } from '~/components/ModalCard';
import { useFeatures } from '~/hooks';
import { colors } from '~/styles';
import isSameRole from './isSameRole';

const StyledTable = styled(Table)`
  font-size: 0.875rem;
  margin-top: 1rem;

  ${Table.Cell} {
    align-items: flex-start;
    height: 100%;
    min-height: unset;
    padding: 1rem;
  }
  opacity: ${({ fade }) => (fade ? 0.1 : 1)};

  height: 25rem;
  overflow-y: auto;
`;

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.5rem;
    margin: 0;
  }
`;

// Wider than the client-role dialog: this one carries a discipline column and three filters.
const StyledModal = styled(ModalCard)`
  ${Card} {
    width: 72rem;
  }
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const StyledHeader = styled(Table.Header)`
  ${({ sticky }) =>
    sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
    `}
`;

function getDuplicateTooltip(role, projectRoles, practicesEnabled) {
  return projectRoles.some((projectRole) => isSameRole(role, projectRole, practicesEnabled))
    ? 'This role already exists for this project.'
    : null;
}

// Workspace roles carry a practice, location and discipline, so this dialog narrows by all three and searches
// across them — the client-role dialog has only a name to go on. A role already on the project stays listed
// but cannot be picked, so its absence from the choices is never mistaken for the role not existing.
export default function AddWorkspaceRolesDialog({ workspaceRoles, projectRoles, onSubmit, onClose }) {
  const features = useFeatures();

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ practice: null, location: null, discipline: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchKeys = useMemo(
    () =>
      [
        'name',
        features.practices && 'practice.name',
        'location.name',
        features.disciplines && 'discipline.name',
      ].filter(Boolean),
    [features.disciplines, features.practices],
  );

  const options = useMemo(() => {
    const result = workspaceRoles.filter((role) => {
      if (features.practices && filters.practice && role.practice?.id !== filters.practice.id) return false;
      if (filters.location && role.location?.id !== filters.location.id) return false;
      if (features.disciplines && filters.discipline && role.discipline?.id !== filters.discipline.id) return false;
      return true;
    });

    if (!searchQuery) return result;

    const fuse = new Fuse(result, { keys: searchKeys, threshold: 0.4 });
    return fuse.search(searchQuery).map((match) => match.item);
  }, [
    features.disciplines,
    features.practices,
    filters.discipline,
    filters.location,
    filters.practice,
    searchKeys,
    searchQuery,
    workspaceRoles,
  ]);

  const selectableOptions = useMemo(
    () => options.filter((role) => !getDuplicateTooltip(role, projectRoles, features.practices)),
    [features.practices, options, projectRoles],
  );

  const selected = useMemo(() => {
    const selectableRoleIds = new Set(selectableOptions.map(({ id }) => id));
    const visibleSelectedRoles = selectedRoles.filter(({ id }) => selectableRoleIds.has(id));
    const sourceRoleIds = new Set(workspaceRoles.map(({ id }) => id));

    return {
      get any() {
        return selectedRoles.some(({ id }) => sourceRoleIds.has(id));
      },
      get visibleSome() {
        return visibleSelectedRoles.length > 0;
      },
      get visibleAll() {
        return selectableOptions.length > 0 && visibleSelectedRoles.length === selectableOptions.length;
      },
    };
  }, [selectableOptions, selectedRoles, workspaceRoles]);

  // The header checkbox acts on what the filters and search have left on screen, not the whole workspace.
  const handleBulkSelectionChange = () => {
    const selectableRoleIds = new Set(selectableOptions.map(({ id }) => id));

    setSelectedRoles((currentSelectedRoles) => {
      if (selected.visibleAll) return currentSelectedRoles.filter(({ id }) => !selectableRoleIds.has(id));

      const selectedRoleIds = new Set(currentSelectedRoles.map(({ id }) => id));
      return [...currentSelectedRoles, ...selectableOptions.filter(({ id }) => !selectedRoleIds.has(id))];
    });
  };

  const handleAdd = async () => {
    setIsSubmitting(true);
    await onSubmit(selectedRoles);
    setIsSubmitting(false);
  };

  const handleSearchChange = ({ target: { value } }) => setSearchQuery(value);

  const handleFilterChange = ({ target: { name, value } }) =>
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }));

  return (
    <StyledModal title="Add Workspace Roles" onClose={onClose}>
      <ModalCard.Body style={{ marginBottom: 0 }}>
        <SearchInput
          value={searchQuery}
          placeholder="All"
          materialPlaceholder="Search"
          materialAlwaysVisible
          onChange={handleSearchChange}
        />

        <Filters>
          {features.practices && (
            <PracticeSelect
              name="practice"
              value={filters.practice}
              placeholder="Practice"
              onChange={handleFilterChange}
              component={FixedDropdown}
            />
          )}
          <LocationSelect
            name="location"
            value={filters.location}
            placeholder="Location"
            onChange={handleFilterChange}
            component={FixedDropdown}
          />
          {features.disciplines && (
            <DisciplineSelect
              name="discipline"
              value={filters.discipline}
              placeholder="Discipline"
              onChange={handleFilterChange}
              component={FixedDropdown}
            />
          )}
        </Filters>

        <StyledTable>
          <StyledHeader sticky>
            <Table.Column align="center" width="2.5rem">
              <StyledCheckbox>
                <Checkbox
                  checked={selected.visibleSome || false}
                  partial={selected.visibleSome && !selected.visibleAll}
                  onChange={handleBulkSelectionChange}
                />
              </StyledCheckbox>
            </Table.Column>
            <Table.Column width="15rem">Role Name</Table.Column>
            <Table.Column isVisible={features.practices} width="12rem">
              Practice
            </Table.Column>
            <Table.Column width="12rem">Location</Table.Column>
            <Table.Column isVisible={features.disciplines} width="12rem">
              Discipline
            </Table.Column>
            <Table.Column align="right">Rate</Table.Column>
          </StyledHeader>
          <Table.Body>
            {options.map((role) => {
              const checked = !!selectedRoles.find((r) => r.id === role.id);
              const tooltip = getDuplicateTooltip(role, projectRoles, features.practices);
              const duplicated = !!tooltip;

              const handleSelectionChange = () => {
                setSelectedRoles((currentSelectedRoles) =>
                  checked ? currentSelectedRoles.filter(({ id }) => id !== role.id) : [...currentSelectedRoles, role],
                );
              };

              return (
                <Table.Row key={role.id}>
                  <Table.Cell>
                    <Tooltip message={tooltip ?? undefined}>
                      <StyledCheckbox>
                        <Checkbox
                          disabled={duplicated}
                          checked={checked}
                          onChange={handleSelectionChange}
                          data-testid={`${role.id}_checkbox`}
                        />
                      </StyledCheckbox>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>{role.name}</Table.Cell>
                  <Table.Cell>{role.practice?.name}</Table.Cell>
                  <Table.Cell>{role.location?.name}</Table.Cell>
                  <Table.Cell>{role.discipline?.name}</Table.Cell>
                  <Table.Cell>
                    <Currency
                      value={role.isBillable ? role.rate : null}
                      maximumFractionDigits={7}
                      currency={role.currency}
                    />
                    <BillableIcon style={{ marginLeft: '0.4rem' }} value={role.isBillable} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </StyledTable>
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button isLoading={isSubmitting} disabled={!selected.any} onClick={handleAdd}>
            Add
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </StyledModal>
  );
}
